import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import Card from '../../Card/Card';
import TabActionButtons from '../../FormElements/TabActionButtons/TabActionButtons';
import ToggleSwitch from '../../FormElements/ToggleSwitch/ToggleSwitch';
import styles from './TabSections.module.css';

const PropertyDetailsSettings = ({ onSave, onReset, settingsData, onSettingsChange, showToast }) => {
  const [settings, setSettings] = useState({
    // User Experience Settings
    hvnly_EnableBreadcrumbs: true,
    hvnly_EnableBackToTop: true,
    hvnly_EnablePrintButton: true,
    hvnly_EnableSaveProperty: true,
    hvnly_EnableShareButton: true,
  });

  // Load settings from props when available
  useEffect(() => {
    if (settingsData && typeof settingsData === 'object' && Object.keys(settingsData).length > 0) {
      const updatedSettings = {
        hvnly_EnableBreadcrumbs: settingsData.hvnly_EnableBreadcrumbs !== undefined 
          ? settingsData.hvnly_EnableBreadcrumbs 
          : settings.hvnly_EnableBreadcrumbs,
        hvnly_EnableBackToTop: settingsData.hvnly_EnableBackToTop !== undefined 
          ? settingsData.hvnly_EnableBackToTop 
          : settings.hvnly_EnableBackToTop,
        hvnly_EnablePrintButton: settingsData.hvnly_EnablePrintButton !== undefined 
          ? settingsData.hvnly_EnablePrintButton 
          : settings.hvnly_EnablePrintButton,
        hvnly_EnableSaveProperty: settingsData.hvnly_EnableSaveProperty !== undefined 
          ? settingsData.hvnly_EnableSaveProperty 
          : settings.hvnly_EnableSaveProperty,
        hvnly_EnableShareButton: settingsData.hvnly_EnableShareButton !== undefined 
          ? settingsData.hvnly_EnableShareButton 
          : settings.hvnly_EnableShareButton,
      };
      setSettings(prev => ({ ...prev, ...updatedSettings }));
    }
  }, [settingsData]);

  // Handle individual toggle changes
  const handleInputChange = (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    
    // Notify parent component of changes
    if (onSettingsChange && typeof onSettingsChange === 'function') {
      onSettingsChange(newSettings);
    }
  };

  // Handle save action
  const handleSave = async () => {
    if (onSave && typeof onSave === 'function') {
      const result = await onSave(settings);
      return result === true || result === undefined;
    }
    return false;
  };

  // Handle reset action
  const handleReset = async () => {
    // Default settings as defined in DefaultSettingsData.php and SettingsSchema.php
    const defaultSettings = {
      hvnly_EnableBreadcrumbs: true,
      hvnly_EnableBackToTop: true,
      hvnly_EnablePrintButton: true,
      hvnly_EnableSaveProperty: true,
      hvnly_EnableShareButton: true,
    };
    
    setSettings(defaultSettings);
    
    // Notify parent component of reset
    if (onSettingsChange && typeof onSettingsChange === 'function') {
      onSettingsChange(defaultSettings);
    }
    
    // Call parent reset handler
    if (onReset && typeof onReset === 'function') {
      await onReset();
      return true;
    }
    return false;
  };

  return (
    <div 
      className={`${styles['hvnly-settings-api__content']} ${styles['hvnly-settings-api__content--active']}`} 
      id="property-details"
    >
      <h2>{__('Single Property Page', 'havenlytics')}</h2>
      <p className="hvnly-settings-description">
        {__('Configure the display and functionality of your single property pages.', 'havenlytics')}
      </p>

      {/* User Experience Card */}
      <Card 
        title={__("User Experience Settings", "havenlytics")} 
        icon="fas fa-user-friends" 
        collapsible
        defaultExpanded={true}
      >
        <div className="hvnly-settings-field-group">
          <ToggleSwitch
            id="hvnly_EnableBreadcrumbs"
            checked={settings.hvnly_EnableBreadcrumbs === true || settings.hvnly_EnableBreadcrumbs === 'true' || settings.hvnly_EnableBreadcrumbs === 1}
            onChange={(checked) => handleInputChange('hvnly_EnableBreadcrumbs', checked)}
            label={__("Enable Breadcrumbs Navigation", "havenlytics")}
          />
          <p className="hvnly-field-description">
            {__('Display breadcrumb navigation to help users navigate back to property listings.', 'havenlytics')}
          </p>
        </div>

        <div className="hvnly-settings-field-group">
          <ToggleSwitch
            id="hvnly_EnableBackToTop"
            checked={settings.hvnly_EnableBackToTop === true || settings.hvnly_EnableBackToTop === 'true' || settings.hvnly_EnableBackToTop === 1}
            onChange={(checked) => handleInputChange('hvnly_EnableBackToTop', checked)}
            label={__("Enable Back to Top Button", "havenlytics")}
          />
          <p className="hvnly-field-description">
            {__('Show a "Back to Top" button that appears when users scroll down the page.', 'havenlytics')}
          </p>
        </div>

        <div className="hvnly-settings-field-group">
          <ToggleSwitch
            id="hvnly_EnablePrintButton"
            checked={settings.hvnly_EnablePrintButton === true || settings.hvnly_EnablePrintButton === 'true' || settings.hvnly_EnablePrintButton === 1}
            onChange={(checked) => handleInputChange('hvnly_EnablePrintButton', checked)}
            label={__("Enable Print Button on Property Pages", "havenlytics")}
          />
          <p className="hvnly-field-description">
            {__('Allow users to print property details directly from the property page.', 'havenlytics')}
          </p>
        </div>

        <div className="hvnly-settings-field-group">
          <ToggleSwitch
            id="hvnly_EnableSaveProperty"
            checked={settings.hvnly_EnableSaveProperty === true || settings.hvnly_EnableSaveProperty === 'true' || settings.hvnly_EnableSaveProperty === 1}
            onChange={(checked) => handleInputChange('hvnly_EnableSaveProperty', checked)}
            label={__("Enable Save Property Button", "havenlytics")}
          />
          <p className="hvnly-field-description">
            {__('Allow registered users to save properties to their favorites list.', 'havenlytics')}
          </p>
        </div>

        <div className="hvnly-settings-field-group">
          <ToggleSwitch
            id="hvnly_EnableShareButton"
            checked={settings.hvnly_EnableShareButton === true || settings.hvnly_EnableShareButton === 'true' || settings.hvnly_EnableShareButton === 1}
            onChange={(checked) => handleInputChange('hvnly_EnableShareButton', checked)}
            label={__("Enable Share Button", "havenlytics")}
          />
          <p className="hvnly-field-description">
            {__('Allow users to share properties on social media platforms.', 'havenlytics')}
          </p>
        </div>
      </Card>

      <TabActionButtons
        onSave={handleSave}
        onReset={handleReset}
        showToast={showToast}
        saveLabel={__("Save Property Details Settings", "havenlytics")}
        resetLabel={__("Reset to Defaults", "havenlytics")}
      />
    </div>
  );
};

export default PropertyDetailsSettings;